/**
 * Persists a returning visitor's region choice in a cookie shared across every region
 * subdomain. This is the mechanism the CNA-1902 decision log calls out as the reason
 * subdomains were chosen over separate domains: a cookie scoped to `.wfm.cinareo.com` is
 * visible from `wfm.cinareo.com` *and* `eu.wfm.cinareo.com`, so a returning user can be
 * routed straight to their region. Two separate domains could never do this.
 *
 * **Scoped to `.wfm.cinareo.com`, not the `.cinareo.com` apex.** Raised by Gulnur on
 * CNA-1902: the apex also covers `stage.cinareo.com` and `prestage.cinareo.com`, which are
 * environments rather than regions. A cookie set there would be read by the other
 * environments — so a region picked in one could redirect a visitor out of the environment
 * they meant to be in. `wfm.cinareo.com` is the narrowest parent that still spans every
 * region host, which is exactly the boundary we want.
 *
 * Kept in step with regions.ts by hand: if a region is ever added outside the
 * `wfm.cinareo.com` tree, this scope has to widen with it or that region silently stops
 * sharing the cookie.
 *
 * Known local-dev limitation: a browser silently refuses to set a cookie whose `domain`
 * does not match or is not a parent of the current host, so on `localhost` this write is a
 * no-op — the redirect still works, but persistence across visits can only be observed on
 * a real `*.wfm.cinareo.com` host. Not worth working around for local dev; flagging so a
 * missing cookie there isn't mistaken for a bug in this code.
 */

const COOKIE_NAME = 'cinareo_region';
const COOKIE_DOMAIN = '.wfm.cinareo.com';
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export const readRegionCookie = (): string | undefined => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

export const writeRegionCookie = (code: string): void => {
  document.cookie = [
    `${COOKIE_NAME}=${encodeURIComponent(code)}`,
    `domain=${COOKIE_DOMAIN}`,
    'path=/',
    `max-age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    // Every region host is HTTPS, so this costs nothing and stops the cookie being sent or
    // set over plain HTTP. The value is not sensitive — it is a region code — but a cookie
    // that steers pre-authentication routing is still worth keeping off cleartext.
    // Raised by Duy on CNA-1906.
    'Secure',
  ].join('; ');
};
